import { FC, useCallback, useContext, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Checkbox, Dropdown, useSimpleInputState } from "capitalrx-components-2.0/lib/forms";
import { HorizontalLine, Link, Message, Messages, useAlert } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Italic, Semibold } from "capitalrx-components-2.0/lib/typography";

import { useUpdateMemberPaymentConfigurationMutation } from "@/App/api/medicarePrescriptionPaymentPlan";
import { ThemedButton } from "@/App/components";
import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";
import { MemberPaymentConfiguration, PaymentMethod } from "@/App/types/medicarePrescriptionPaymentPlan";
import { getPaymentDayOptionsArray } from "@/App/utils/dates";
import { getAllowedPaymentMethods, getCustomerSupportNumber } from "@/App/utils/utils";
import { StripeIntentForm, StripeSetupElement } from "../../Stripe/StripeIntentForm";
import { usePlanYear } from "../../PlanYearContext";

interface ModifyPaymentInformationFormProps {
  onSubmit: () => void;
  onCancel: () => void;
  paymentConfiguration: MemberPaymentConfiguration;
}

const TRANSLATION_PATH = "MEDICARE_PRESCRIPTION_PAYMENT_PLAN.modifyPaymentInformationForm";

export const ModifyPaymentInformationForm: FC<ModifyPaymentInformationFormProps> = ({ onCancel, onSubmit, paymentConfiguration }) => {
  const [hasLoadingError, setHasLoadingError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { t: translate, i18n } = useTranslation();

  const submitRef = useRef<HTMLButtonElement>();
  const { isViewingDataholder, memberEligibility } = useContext(MemberEligibilityContext);
  const planYear = usePlanYear();
  const customerSupportNumber = getCustomerSupportNumber(memberEligibility);
  const allowedPaymentMethods = getAllowedPaymentMethods(memberEligibility);
  const [paymentMethod, setPaymentMethod] = useSimpleInputState<PaymentMethod>(paymentConfiguration.preferred_payment_method);
  const [paymentDay, setPaymentDay] = useSimpleInputState<number>(+paymentConfiguration.preferred_payment_day);
  const [hasAgreedToTerms, setHasAgreedToTerms] = useSimpleInputState<boolean>(false);
  const showAlert = useAlert();

  const isEmulating = sessionStorage.getItem("isEmulation");
  const shouldDisableStripeComponent = isViewingDataholder || isEmulating;

  const { mutateAsync: updatePaymentConfiguration } = useUpdateMemberPaymentConfigurationMutation();

  // payment day must start at 5 to align with the invoice cycle for monthly billing, which are generated on the 3rd
  const paymentDayMin = 5;
  const paymentDayisWrong = !paymentDay || paymentDay < paymentDayMin;

  const handlePaymentUpdate = async (day: undefined | number, method: PaymentMethod, customerId?: string) => {
    if (paymentMethod !== "check" && paymentDayisWrong) {
      return;
    }
    await updatePaymentConfiguration({
      preferred_payment_day: method !== "check" ? day : null,
      preferred_payment_method: method,
      vendor_customer_id: customerId ? customerId : null,
      plan_year: planYear,
    });
    onSubmit();
  };

  const handleSavePaymentConfiguration = useCallback(async () => {
    setIsLoading(true);
    if (!hasAgreedToTerms) {
      return;
    }
    if (paymentMethod == "check") {
      await handlePaymentUpdate(undefined, paymentMethod);
    } else {
      submitRef.current?.click();
    }
  }, [submitRef, hasAgreedToTerms, paymentMethod]);

  const handleLoadingError = () => {
    setHasLoadingError(true);
  };

  const onStripeError = useCallback(() => {
    showAlert({
      message: translate(`${TRANSLATION_PATH}.unableToContactPaymentVendor`),
      type: "error",
      style: "banner",
    });
    setPaymentMethod("check", paymentMethod);
  }, [paymentMethod, setPaymentMethod]);

  const shouldDisableFormSubmitButton = !hasAgreedToTerms || isLoading || (paymentMethod !== "check" && paymentDayisWrong);

  const paymentDayOptionsArray = getPaymentDayOptionsArray(
    (month_day: string) => <Trans
      i18nKey={`${TRANSLATION_PATH}.monthDayOption`}
      values={{ month_day }}
    />,
    i18n.language,
  );

  const renderStripeCardPaymentForm = () => {
    if (shouldDisableStripeComponent) {
      return <Italic>{translate(`${TRANSLATION_PATH}.memberPaymentDetailsNotDisplayed`)}</Italic>;
    }

    if (hasLoadingError) {
      return (
        <Message appearance={Messages.Error} visible={true}>
          {translate(`${TRANSLATION_PATH}.unableToLoadStripe`)}{" "}
          <a href={`tel:${customerSupportNumber}`}>{customerSupportNumber}</a>.
        </Message>
      );
    }

    return (
      <>
        <StripeSetupElement onError={onStripeError} paymentMethod={paymentMethod}>
          <StripeIntentForm
            submitRef={submitRef}
            paymentMethod={paymentMethod}
            paymentDay={paymentDay}
            handleSavePaymentDetails={handlePaymentUpdate}
            setIsLoading={setIsLoading}
            handleLoadingError={handleLoadingError}
          />
        </StripeSetupElement>
      </>
    );
  };

  return (
    <>
      <HorizontalLine />
      <Stack>
        <Body size="medium">
          <Semibold>{translate(`${TRANSLATION_PATH}.changePaymentMethod`)}</Semibold>
        </Body>
        <Grid>
          <Grid.Span columns={6}>
            <Dropdown
              testId="change-payment-method"
              label={translate(`${TRANSLATION_PATH}.paymentMethod`)}
              value={paymentMethod}
              onChange={setPaymentMethod}
              placeholder={translate(`${TRANSLATION_PATH}.select`)}
            >
              {allowedPaymentMethods.includes("check") && (
                <Dropdown.Option key="check" value="check">
                  {translate(`${TRANSLATION_PATH}.check`)}
                </Dropdown.Option>
              )}
              {allowedPaymentMethods.includes("card") && (
                <Dropdown.Option key="card" value="card">
                  {translate(`${TRANSLATION_PATH}.card`)}
                </Dropdown.Option>
              )}
              {allowedPaymentMethods.includes("ach") && (
                <Dropdown.Option key="ach" value="ach">
                  {translate(`${TRANSLATION_PATH}.bankAccountAch`)}
                </Dropdown.Option>
              )}
            </Dropdown>
          </Grid.Span>

          {paymentMethod !== "check" && (
            <Grid.Span columns={6}>
              <Dropdown
                testId="payment-date"
                value={paymentDay}
                onChange={setPaymentDay}
                label={translate(`${TRANSLATION_PATH}.automaticPaymentDate`)}
                instructions={translate(`${TRANSLATION_PATH}.automaticPaymentDateDescription`)}
                placeholder={translate(`${TRANSLATION_PATH}.select`)}
              >
                {paymentDayOptionsArray.map(({ key, label }) => {
                  return (
                    <Dropdown.Option key={key} value={key}>
                      {label}
                    </Dropdown.Option>
                  );
                })}
              </Dropdown>
            </Grid.Span>
          )}
        </Grid>
        {paymentMethod !== "check" && renderStripeCardPaymentForm()}
        <Checkbox
          testId="payment-methods-terms-and-conditions"
          value={hasAgreedToTerms}
          label={
            <Trans
              i18nKey={`${TRANSLATION_PATH}.paymentMethodAgreement`}
              components={{ TermsAndConditionsLink: (
                <Link to="https://www.cap-rx.com/legal#T-C">{translate(`${TRANSLATION_PATH}.termsAndConditions`)}</Link>
              ) }}
            />
          }
          hideLabel
          onChange={setHasAgreedToTerms}
        />
        <Shelf spacing="small">
          <ThemedButton
            testId="save-payment"
            onClick={handleSavePaymentConfiguration}
            appearance="primary"
            disabled={shouldDisableFormSubmitButton}
          >
            {translate(`${TRANSLATION_PATH}.save`)}
          </ThemedButton>
          <ThemedButton testId="cancel-payment" appearance="secondary" onClick={onCancel}>
            {translate(`${TRANSLATION_PATH}.cancel`)}
          </ThemedButton>
          <Shelf.Spacer />
        </Shelf>
      </Stack>
      <HorizontalLine />
    </>
  );
};
